import { ReactElement, MouseEvent, useEffect, useState } from 'react';
import useStyles from './styles';
import { Trans, useTranslation } from 'react-i18next';
import { BACKEND_URL, INVENTORY_URL } from 'api';
import { isMobile, loadSdsCache, renderSnackbar } from 'utils';
import {
  LiteralInterface,
  LoadSdsCacheDto,
  RowPerPageType,
  SDSInterface,
} from 'interfaces';
import APP_PAGE from 'enums/page.enums';
/* Components */
import {
  Alert,
  Button,
  CircularProgress,
  ClassNameMap,
  Menu,
  MenuItem,
  TablePagination,
  Typography,
} from '@mui/material';
import {
  TravelExplore,
  MoreHoriz,
  Info as InfoIcon,
} from '@mui/icons-material';
import SubstanceDetailPopup from 'components/popup/sds-detail/substance-detail-popup';
import CardTable from 'components/card-table/card-table-desktop';
import SearchBar from 'components/search-bar';
import CardTableMobile from 'components/card-table/card-table-mobile';
import CustomTooltip from 'components/custom-tooltip';
import CardTableLoadingDesktop from 'components/card-table/card-table-desktop/card-table-loading';
import CardTableLoadingMobile from 'components/card-table/card-table-mobile/card-table-loading';
import CodeScanner from 'components/code-scanner';

interface ActionMenuOption {
  id: string;
  title: string;
  onClick: () => void;
  tooltip?: string;
}

interface MySDSProps {
  lastUpdated: string | null;
  literal: LiteralInterface | null;
  status: string | null;
  onRefresh: () => void;
  onRenew: () => Promise<void>;
  onSwitchPage: (name: APP_PAGE, defaultSearch?: string) => void;
  syncing: boolean;
}

const MySDS = ({
  lastUpdated,
  literal,
  status,
  onRefresh,
  onRenew,
  onSwitchPage,
  syncing,
}: MySDSProps) => {
  const { t } = useTranslation();
  const classes: ClassNameMap = useStyles();
  const [detailPopup, setDetailPopup] = useState<ReactElement | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>('');
  const [tableData, setTableData] = useState<Array<SDSInterface> | null>(null);
  const [dataCount, setDataCount] = useState<number | null>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<RowPerPageType>(10);
  const [menuActionAnchor, setMenuActionAnchor] =
    useState<null | HTMLButtonElement>(null);
  const openActionMenu: boolean = Boolean(menuActionAnchor);
  const token = localStorage.getItem('access_token');

  const searchSds = async (
    searchText: string,
    offset: number,
    pageSize: number,
  ): Promise<void> => {
    setLoading(true);
    loadSdsCache(searchText, offset, pageSize)
      .then((response: LoadSdsCacheDto) => {
        setDataCount(response.count);
        setTableData(response.data);
        setLoading(false);
      })
      .catch(err => {
        renderSnackbar('Something went wrong');
        setLoading(false);
      });
  };

  const renderActonBtnGroup = (): ReactElement => {
    const menuActionOptions: Array<ActionMenuOption> = [
      {
        id: 'get-latest-change',
        title: t('common:get_latest_change'),
        tooltip: t('common:get_latest_change_tooltip'),
        onClick: onRefresh,
      },
      {
        id: 'renew-data',
        title: t('common:renew_data'),
        tooltip: t('common:renew_data_tooltip'),
        onClick: () => {
          if (window.confirm(t('common:renew_confirm_text')) === true)
            onRenew();
        },
      },
    ];

    return (
      <div className={classes.headerActionGroup}>
        <div className={classes.headerActionBtnGroup}>
          {token && isMobile() && (
            <Typography
              component="a"
              target="_blank"
              href={`${INVENTORY_URL}/my-sds/?qr_login_token=${token}`}
            >
              {t('common:open_web_version')}
            </Typography>
          )}

          <CustomTooltip title={t('common:global_sds_search')} placement="top">
            <TravelExplore
              onClick={() => onSwitchPage(APP_PAGE.GLOBAL_SEARCH_SDS)}
              className={classes.actionIcon}
            />
          </CustomTooltip>

          <Button
            onClick={e => setMenuActionAnchor(e.currentTarget)}
            sx={{
              fontSize: '12px',
              padding: '2px 8px',
              backgroundColor: '#fff',
              fontWeight: 600,
            }}
            variant={'outlined'}
            color={'primary'}
          >
            <MoreHoriz width={12} height={12} />
          </Button>
        </div>

        {status ? (
          <div className={classes.progressStatus}>
            <CircularProgress style={{ color: '#898CFA' }} size={18} />
            <Typography style={{ color: '#898CFA' }} variant="button">
              {status}
            </Typography>
          </div>
        ) : lastUpdated ? (
          <Typography variant={isMobile() ? 'caption' : 'body1'}>
            {lastUpdated}
          </Typography>
        ) : null}

        <Menu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          anchorEl={menuActionAnchor}
          open={openActionMenu}
          onClose={e => setMenuActionAnchor(null)}
          className={classes.actionGroupMenu}
        >
          {menuActionOptions.map(option => (
            <MenuItem
              key={option.id}
              onClick={(e: MouseEvent<HTMLLIElement>) => {
                e.preventDefault();
                e.stopPropagation();
                option.onClick();
                setMenuActionAnchor(null);
              }}
            >
              {option.tooltip ? (
                <CustomTooltip title={option.tooltip} placement="left">
                  <span>{option.title}</span>
                </CustomTooltip>
              ) : (
                <span>{option.title}</span>
              )}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  const renderSubstanceList = (): ReactElement => {
    if (tableData === null || dataCount === null) return renderLoading();

    if (dataCount < 1) {
      return (
        <div className={classes.noSearchDataAlert}>
          <Alert
            severity="info"
            color="warning"
            sx={{
              width: isMobile() ? 'auto' : '700px',
              borderRadius: '5px',
              border: '2px solid #b0840c',
              padding: '10px',
            }}
            icon={<InfoIcon fontSize="inherit" sx={{ color: '#b0840c' }} />}
          >
            <Typography
              sx={{ fontSize: '16px', color: 'black', fontWeight: '600' }}
            >
              <Trans
                ns={'common'}
                i18nKey={'did_not_match_sds_in_library'}
                values={{ keyword: searchText }}
                components={[<span style={{ backgroundColor: 'yellow' }} />]}
              />
            </Typography>
          </Alert>
          <Typography
            component="a"
            href="#"
            onClick={() => onSwitchPage(APP_PAGE.GLOBAL_SEARCH_SDS, searchText)}
          >
            {' '}
            {t('common:redo_search_in_global_database')}{' '}
          </Typography>
        </div>
      );
    }

    return (
      <>
        {loading ? (
          renderLoading()
        ) : isMobile() ? (
          <div className={classes.cardTableContainer}>
            <CardTableMobile
              data={tableData}
              onViewSubstanceDetail={(data, toSection) =>
                setDetailPopup(
                  <SubstanceDetailPopup
                    sds={data}
                    literal={literal}
                    onClose={() => setDetailPopup(null)}
                    toSection={toSection}
                  />,
                )
              }
            />
          </div>
        ) : (
          <div className={classes.cardTableContainer}>
            <CardTable
              data={tableData}
              onViewSubstanceDetail={(data, toSection) =>
                setDetailPopup(
                  <SubstanceDetailPopup
                    sds={data}
                    literal={literal}
                    onClose={() => setDetailPopup(null)}
                    toSection={toSection}
                  />,
                )
              }
            />
          </div>
        )}

        <div className={classes.cardTableActionContainer}>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={dataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, value) => {
              setPage(value);
              searchSds(searchText, value, rowsPerPage);
            }}
            onRowsPerPageChange={e => {
              setRowsPerPage(+e.target.value as RowPerPageType);
              setPage(0);
              searchSds(searchText, 0, +e.target.value as RowPerPageType);
            }}
            style={{
              backgroundColor: '#F2F5F7',
              display: 'flex',
            }}
            labelRowsPerPage={`${t('common:rows_per_page')}`}
          />
        </div>
      </>
    );
  };

  const renderLoading = (): ReactElement => {
    if (isMobile()) return <CardTableLoadingMobile />;
    return <CardTableLoadingDesktop rowCount={2} />;
  };

  useEffect(() => {
    if (!syncing) {
      searchSds('', page, rowsPerPage);
      return;
    }
    if (tableData !== null) return;
    searchSds('', page, rowsPerPage);
  }, [syncing]);

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <div className={classes.logoContainer}>
            <img
              alt="logo"
              src={
                literal?.logo
                  ? `${BACKEND_URL}${literal.logo}`
                  : '/images/logo.svg'
              }
              className={classes.logo}
            />
          </div>
          {token && !isMobile() && (
            <Typography
              component="a"
              target="_blank"
              href={`${INVENTORY_URL}/my-sds/?qr_login_token=${token}`}
            >
              {t('common:open_web_version')}
            </Typography>
          )}
          {renderActonBtnGroup()}
        </div>
        <div className={classes.subHeader}>
          <div className={classes.pageTitle}>
            {t('common:sds_library_for')}
            <span style={{ fontWeight: 600 }}>{literal?.name}</span>
          </div>
          {isMobile() && (
            <CodeScanner
              country={literal?.country}
              ean={searchText}
              setEan={(text: string) => {
                setLoading(true);
                setSearchText(text);
                searchSds(text, page, rowsPerPage);
              }}
              upc={searchText}
              setUpc={(text: string) => {
                setLoading(true);
                setSearchText(text);
                searchSds(text, page, rowsPerPage);
              }}
              onSearch={(text: string) => {
                setLoading(true);
                setSearchText(text);
                searchSds(text, page, rowsPerPage);
              }}
              customViewportId={'interactive-search'}
              size={40}
            />
          )}
        </div>
        <div className={classes.searchBarWrapper}>
          <SearchBar
            id="search-input"
            placeHolder={t('common:search_placeholder')}
            onSearch={text => {
              setLoading(true);
              setSearchText(text);
              searchSds(text, page, rowsPerPage);
            }}
            onClear={() => {
              setLoading(true);
              setSearchText('');
              searchSds('', page, rowsPerPage);
            }}
            onChange={text => setSearchText(text)}
            defaultValue={searchText}
            searchValue={searchText}
          />
        </div>

        {syncing ? renderLoading() : renderSubstanceList()}
      </div>

      {detailPopup}
    </>
  );
};

export default MySDS;
